/* eslint-disable max-lines-per-function */
import React, { JSX, useEffect, useState } from 'react';
import Select, { components, createFilter, DropdownIndicatorProps } from 'react-select';
import Styles from './ReactInputSelect.module.scss';
import { DISABLED_BACKGROUND, ERROR_BORDER } from '../../utils/constants';

interface ReactInputSelectSchema {
  name: string;
  options: any[];
  content: any;
  optionsLabel?: string;
  optionsValue?: string;
  defaultValue: any;
  disableValues?: string[];
  autoSelectValueFromOptions?: boolean;
  disabled?: boolean;
  onChange?: (value: any) => void;
  onSaveClicked?: boolean;
  menuPlacement?: 'bottom' | 'top' | 'auto';
  loginIcons?: any;
}

const defaultVal = {};

const ReactInputSelect = React.forwardRef<any, ReactInputSelectSchema>(
  (
    {
      name,
      options,
      content,
      disableValues = [],
      defaultValue = defaultVal,
      optionsValue = 'value',
      optionsLabel = 'label',
      disabled = false,
      autoSelectValueFromOptions = true,
      onChange,
      onSaveClicked = false,
      menuPlacement,
      loginIcons = ''
    },
    ref
  ): JSX.Element => {
    const [hasInputclicked, setInputclicked] = useState<boolean>(false);
    const [selectedOption, setSelectedOption] = useState<any>(defaultValue);
    const [isFocus, setIsFocus] = useState<boolean>(false);
    const [isError, setError] = useState<boolean>(false);

    const handleChange = (value: any) => {
      setSelectedOption(value);
      if (typeof onChange === 'function') {
        if (value) onChange({ name: name, value: value });
        else onChange({ name: name, value: { [optionsValue]: '', [optionsLabel]: '' } });
      }
    };

    useEffect(() => {
      for (let i = 0; i < options.length; i++) {
        if (options[i][optionsValue] === selectedOption?.[optionsValue]) {
          handleChange({
            [optionsValue]: options[i][optionsValue],
            [optionsLabel]: options[i][optionsLabel]
          });
          break;
        }
      }
    }, [options[0]?.[optionsLabel]]);

    useEffect(() => {
      if (defaultValue && autoSelectValueFromOptions) setSelectedOption(defaultValue);
    }, [defaultValue]);

    const DropdownIndicator = (props: DropdownIndicatorProps<any>): JSX.Element => {
      const Component = components.DropdownIndicator as React.ComponentType<any>;
      return (
        <Component {...props}>
          <span
            className={`${Styles.downArrowIcon} ${
              props.selectProps.menuIsOpen ? Styles.transform180 : ''
            }`}
          >
            <img src={loginIcons?.chevronDown?.filename} alt='chevronDown' />
          </span>
        </Component>
      );
    };

    const handleBlur = () => {
      !hasInputclicked && setInputclicked(true);
      setIsFocus(false);
    };

    if (Boolean(selectedOption) == isError) {
      setError(!isError);
    }

    const customStyles = (hasError: boolean) => ({
      control: (styles: any) => ({
        ...styles,
        ...(hasError && { border: '2px solid ' + ERROR_BORDER + ' !important' }),
        ...(disabled && { background: DISABLED_BACKGROUND + ' !important', border: 'none' })
      }),
      valueContainer: (styles: any) => ({
        ...styles,
        ...(disabled && { padding: '4px 10px 4px !important' })
      })
    });

    const filterConfig: any = {
      ignoreCase: true,
      ignoreAccents: true,
      trim: true,
      matchFrom: 'start'
    };

    
    return (
      <div className={Styles.selectcontainer}>
        <Select
          className={`react-select-container ${isError && 'errorborder'}`}
          name={name}
          instanceId='React-select'
          classNamePrefix='react-select'
          components={{ DropdownIndicator, IndicatorSeparator: null }}
          value={selectedOption}
          id={name}
          onChange={handleChange}
          isClearable
          placeholder={''}
          menuPlacement={menuPlacement}
          onFocus={() => setIsFocus(true)}
          onBlur={handleBlur}
          backspaceRemovesValue={true}
          options={options}
          ref={ref}
          getOptionLabel={(option: any) => option[optionsLabel]}
          getOptionValue={(option: any) => option[optionsValue]}
          filterOption={createFilter(filterConfig)}
          isOptionDisabled={(option: any) => disableValues.includes(option[optionsValue])}
          isDisabled={disabled}
          isSearchable={false}
        />
        {!disabled && content?.placeholder && (
          <label
            className={`${
              ((selectedOption && selectedOption[optionsValue] && selectedOption[optionsLabel]) ||
                isFocus) &&
              'activeLabel'
            } selectcomplabel`}
          >
            {content?.placeholder}
          </label>
        )}
        {isError && hasInputclicked && defaultValue?.id === '' && content?.errorMessage && (
          <span className={Styles.errormessage}>{content?.errorMessage}</span>
        )}
      </div>
    );
  }
);

export default ReactInputSelect;
